import { useState } from "react";
import type { FormEvent } from "react";
import { upsertTeams } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface CreateSeasonFormProps {
  idToken: string;
  /** Called once the season + manifest have been created. */
  onCreated: (season: number) => void;
}

/** Same "one row per team" editor as `EditTeamsForm`, plus a season number —
 *  there's no existing archive yet to source a season/roster from. */
export function CreateSeasonForm({ idToken, onCreated }: CreateSeasonFormProps) {
  const [season, setSeason] = useState(new Date().getFullYear());
  const [names, setNames] = useState<string[]>(["", ""]);
  const { status, error, submit } = useAdminForm(async () => {
    const teams = names.map((n) => n.trim()).filter(Boolean).map((name) => ({ name }));
    if (teams.length < 2) {
      throw new Error("Add at least two teams to get started.");
    }
    await upsertTeams(season, teams, idToken);
    return season;
  }, onCreated);

  function updateName(index: number, value: string) {
    setNames((prev) => prev.map((n, i) => (i === index ? value : n)));
  }

  function removeRow(index: number) {
    setNames((prev) => prev.filter((_, i) => i !== index));
  }

  function addRow() {
    setNames((prev) => [...prev, ""]);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  return (
    <section className="admin-panel" aria-label="Set up the first season">
      <h2 className="feature__title">Set up your first season</h2>
      <p className="feature__description">
        No results have been published yet. Enter this season's teams to get started —
        you'll add weekly results afterward.
      </p>

      <form className="admin-panel__form" onSubmit={handleSubmit}>
        <div className="admin-panel__field admin-panel__field--narrow">
          <label htmlFor="create-season">Season</label>
          <input
            id="create-season"
            type="number"
            required
            value={season}
            onChange={(e) => setSeason(Number(e.target.value))}
          />
        </div>

        <ul className="admin-panel__team-list">
          {names.map((name, i) => (
            <li key={i} className="admin-panel__team-row">
              <input
                type="text"
                placeholder="Team name"
                value={name}
                onChange={(e) => updateName(i, e.target.value)}
              />
              <button
                type="button"
                className="admin-panel__remove"
                aria-label={`Remove ${name || "team"}`}
                onClick={() => removeRow(i)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>

        <div className="admin-panel__actions">
          <button type="button" onClick={addRow}>
            + Add team
          </button>
          <button type="submit" disabled={status === "pending"}>
            {status === "pending" ? "Creating…" : "Create season"}
          </button>
          <AdminFormStatus status={status} error={error} />
        </div>
      </form>
    </section>
  );
}
